// src/pages/help/HelpPage.jsx
import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const HelpPage = () => {
  const { vendorProfile, currentUser } = useAuth();
  const { showAlert } = useNotification();
  
  const [activeSection, setActiveSection] = useState('getting-started');
  const [contactForm, setContactForm] = useState({
    name: vendorProfile?.shopName || '',
    email: currentUser?.email || '',
    subject: '',
    priority: 'medium',
    category: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // FAQ data
  const faqData = {
    'getting-started': [
      {
        question: 'How do I add my first product?',
        answer: 'Navigate to Inventory → Add Product. Fill in the product details including name, description, price, and upload images. Make sure to set appropriate categories and stock levels.'
      },
      {
        question: 'How do I manage my shop profile?',
        answer: 'Go to Settings → Profile to update your shop information, contact details, business hours, and verification documents.'
      },
      {
        question: 'What payment methods are supported?',
        answer: 'We support UPI, Credit/Debit Cards, Net Banking, and Cash on Delivery. You can configure your preferred payment methods in Settings.'
      }
    ],
    'inventory': [
      {
        question: 'How do I track low stock items?',
        answer: 'Set low stock thresholds for each product. The system will automatically notify you when stock falls below the threshold. Check the Dashboard for low stock alerts.'
      },
      {
        question: 'Can I bulk upload products?',
        answer: 'Yes, use the Bulk Upload feature in Inventory. Download the CSV template, fill in your product data, and upload the file.'
      },
      {
        question: 'How do I organize products by categories?',
        answer: 'Create categories in Inventory → Categories. You can create main categories and subcategories for better organization.'
      }
    ],
    'orders': [
      {
        question: 'How do I process incoming orders?',
        answer: 'New orders appear in Orders → Pending. Review order details, confirm availability, and update status to Confirmed. Then process for shipping.'
      },
      {
        question: 'Can I modify order status?',
        answer: 'Yes, you can update order status through the order details page. Available statuses: Pending → Confirmed → Processing → Shipped → Delivered.'
      },
      {
        question: 'How do I handle order cancellations?',
        answer: 'You can cancel orders before shipping. Go to the order details and select Cancel. Provide a reason for cancellation.'
      }
    ],
    'porter': [
      {
        question: 'How do I dispatch orders via Porter?',
        answer: 'In the order details page, click "Dispatch via Porter". Enter pickup and delivery addresses, get quotes, select vehicle type, and confirm the delivery.'
      },
      {
        question: 'How do I track Porter deliveries?',
        answer: 'Use the Porter page to view all deliveries. Click "Track" to open Porter\'s tracking page, or monitor status updates in real-time.'
      },
      {
        question: 'What if a Porter delivery fails?',
        answer: 'Failed deliveries will show as "Cancelled" status. Contact Porter support or create a new delivery order if needed.'
      }
    ],
    'payments': [
      {
        question: 'When do I receive payments?',
        answer: 'Payments are processed within 3-5 business days after order delivery confirmation. Check your payment dashboard for details.'
      },
      {
        question: 'What are the commission charges?',
        answer: 'Commission rates vary by product category, typically 2-8%. Check your vendor agreement or contact support for specific rates.'
      },
      {
        question: 'How do I update my bank details?',
        answer: 'Go to Settings → Payment Information to update bank account details. Changes require verification and may take 2-3 days to process.'
      }
    ],
    'technical': [
      {
        question: 'The app is running slowly, what should I do?',
        answer: 'Clear your browser cache, ensure stable internet connection, and try refreshing the page. If issues persist, contact technical support.'
      },
      {
        question: 'I cannot upload product images',
        answer: 'Ensure images are in JPG/PNG format, under 5MB each. Check your internet connection and try uploading one image at a time.'
      },
      {
        question: 'How do I reset my password?',
        answer: 'Use the "Forgot Password" link on the login page, or go to Settings → Security to change your password.'
      }
    ]
  };

  // Help sections
  const helpSections = [
    { id: 'getting-started', name: 'Getting Started', icon: '🚀' },
    { id: 'inventory', name: 'Inventory Management', icon: '📦' },
    { id: 'orders', name: 'Order Processing', icon: '🛒' },
    { id: 'porter', name: 'Porter Deliveries', icon: '🚚' },
    { id: 'payments', name: 'Payments & Billing', icon: '💳' },
    { id: 'technical', name: 'Technical Support', icon: '🔧' }
  ];

  // Contact categories
  const contactCategories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'porter', label: 'Porter Delivery Issue' },
    { value: 'account', label: 'Account & Profile' },
    { value: 'product', label: 'Product & Inventory' },
    { value: 'feedback', label: 'Feedback & Suggestions' }
  ];

  // Priority levels
  const priorityLevels = [
    { value: 'low', label: 'Low', color: 'text-green-700 bg-green-100' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-700 bg-yellow-100' },
    { value: 'high', label: 'High', color: 'text-orange-700 bg-orange-100' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-700 bg-red-100' }
  ];

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // TODO: Implement actual form submission to backend
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      
      showAlert('Your query has been submitted successfully. We will contact you within 24 hours.', 'success');
      
      // Reset form
      setContactForm({
        name: vendorProfile?.shopName || '',
        email: currentUser?.email || '',
        subject: '',
        priority: 'medium',
        category: 'general',
        message: ''
      });
    } catch (error) {
      showAlert('Failed to submit your query. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter FAQs based on search
  const filteredFAQs = searchQuery 
    ? Object.entries(faqData).reduce((acc, [section, faqs]) => {
        const filtered = faqs.filter(faq => 
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (filtered.length > 0) {
          acc[section] = filtered;
        }
        return acc;
      }, {})
    : { [activeSection]: faqData[activeSection] };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Help & Support</h1>
          <p className="mt-2 text-lg text-gray-600">
            Get help with your ZoneXVendors account and find answers to common questions.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium">Call Support</h3>
                <p className="text-orange-100">+91 8080-123-456</p>
                <p className="text-sm text-orange-200">Mon-Sat, 9 AM - 8 PM</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium">Live Chat</h3>
                <p className="text-blue-100">Chat with our team</p>
                <p className="text-sm text-blue-200">Available 24/7</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium">Email Support</h3>
                <p className="text-green-100">support@zonexvendors.com</p>
                <p className="text-sm text-green-200">Response within 4 hours</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FAQ Section */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Frequently Asked Questions</h2>
                
                {/* Search FAQs */}
                <div className="mt-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Search FAQs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Help Categories */}
              {!searchQuery && (
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {helpSections.map((section) => (
                      <button
                        key={section.id}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeSection === section.id
                            ? 'bg-orange-100 text-orange-700'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                        }`}
                        onClick={() => setActiveSection(section.id)}
                      >
                        <span className="mr-2">{section.icon}</span>
                        {section.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Content */}
              <div className="px-6 py-4">
                {Object.entries(filteredFAQs).map(([sectionId, faqs]) => (
                  <div key={sectionId} className="space-y-4">
                    {searchQuery && (
                      <h3 className="text-lg font-medium text-gray-900 capitalize mb-4">
                        {helpSections.find(s => s.id === sectionId)?.name}
                      </h3>
                    )}
                    {faqs.map((faq, index) => (
                      <details key={index} className="group">
                        <summary className="flex justify-between items-center cursor-pointer p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                          <h4 className="font-medium text-gray-900">{faq.question}</h4>
                          <svg className="h-5 w-5 text-gray-500 group-open:rotate-180 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </summary>
                        <div className="mt-3 p-4 text-gray-700 bg-white rounded-lg border border-gray-200">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                ))}

                {Object.keys(filteredFAQs).length === 0 && (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2 text-gray-500">No FAQs found matching your search.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="space-y-6">
            {/* Contact Us Form */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Contact Us</h2>
                <p className="text-sm text-gray-600 mt-1">Can't find what you're looking for? Send us a message.</p>
              </div>

              <form onSubmit={handleContactSubmit} className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    value={contactForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    value={contactForm.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    value={contactForm.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    {contactCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Priority</label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    value={contactForm.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    {priorityLevels.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    value={contactForm.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    required
                    placeholder="Brief description of your issue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    value={contactForm.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    required
                    placeholder="Please describe your issue in detail..."
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                      Submitting...
                    </div>
                  ) : (
                    'Submit Query'
                  )}
                </Button>
              </form>
            </div>

            {/* Additional Resources */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Additional Resources</h3>
              </div>
              <div className="px-6 py-4 space-y-3">
                <a href="#" className="flex items-center text-sm text-orange-600 hover:text-orange-800">
                  <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  User Guide & Documentation
                </a>
                <a href="#" className="flex items-center text-sm text-orange-600 hover:text-orange-800">
                  <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Video Tutorials
                </a>
                <a href="#" className="flex items-center text-sm text-orange-600 hover:text-orange-800">
                  <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Terms & Conditions
                </a>
                <a href="#" className="flex items-center text-sm text-orange-600 hover:text-orange-800">
                  <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Privacy Policy
                </a>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">System Status</h3>
              </div>
              <div className="px-6 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Platform Status</span>
                  <span className="flex items-center text-sm text-green-600">
                    <span className="h-2 w-2 bg-green-400 rounded-full mr-2"></span>
                    Operational
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Porter Integration</span>
                  <span className="flex items-center text-sm text-green-600">
                    <span className="h-2 w-2 bg-green-400 rounded-full mr-2"></span>
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Payment Gateway</span>
                  <span className="flex items-center text-sm text-green-600">
                    <span className="h-2 w-2 bg-green-400 rounded-full mr-2"></span>
                    Online
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpPage;